'use client'

import { CountdownTimer } from '@/components/common/count-down-timer'
import { cn } from '@/lib/utils'
import { FlashSale } from '@/types/flash-sale'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'

type FlashSaleTabProps = {
  item: FlashSale
  index: number
  type: 'current' | 'next'
  title: string
  activeTab?: string
  setActiveTab?: (tab: string) => void
  bgColor: string
  textColor: string
  borderColor: string
}

const FlashSaleTab = ({
  item,
  index,
  type,
  title,
  activeTab,
  setActiveTab,
  bgColor,
  textColor,
  borderColor,
}: FlashSaleTabProps) => {
  const t = useTranslations()
  const itemId = `${type}-${index}`
  const isActive = activeTab === itemId

  return (
    <div
      className={cn(
        `cursor-pointer rounded-t-2xl ${bgColor} px-3 py-2 text-center md:px-8`,
        isActive ? `border-b-2 border-${borderColor}` : ''
      )}
      onClick={() => setActiveTab && setActiveTab(itemId)}
    >
      <div className={`text-xs font-normal md:text-sm ${textColor}`}>
        {t(title)}
      </div>
      <div className={`text-base font-medium md:text-2xl ${textColor}`}>
        {formatFlashSaleTime(item)}
      </div>
    </div>
  )
}

const formatFlashSaleTime = (item: FlashSale): string => {
  const startTime = dayjs(item.starts_at)
  const endTime = dayjs(item.ends_at)

  return `${startTime.format('HH:mm')} - ${endTime.format('HH:mm')}`
}

interface FlashSaleListProps {
  current: FlashSale[]
  next: FlashSale[]
  activeTab?: string
  setActiveTab?: (tab: string) => void
}

const FlashSaleList = ({
  current,
  next,
  activeTab,
  setActiveTab,
}: FlashSaleListProps) => {
  const t = useTranslations()
  const handleItemClick = (id: string) => {
    if (setActiveTab) {
      setActiveTab(id)
    }
  }

  return (
    <div className='mt-1 flex gap-10'>
      {current.length > 0
        ? current.map((item, index) => (
            <div
              key={`current-${index}`}
              className='flex flex-col items-center font-criteria'
              onClick={() => handleItemClick(`current-${index}`)}
            >
              <FlashSaleTab
                item={item}
                index={index}
                type='current'
                title={t('flash_sale_hardcode.banner.current')}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                bgColor='bg-white'
                textColor='text-black'
                borderColor='border-black'
              />
            </div>
          ))
        : null}
      {next.length > 0
        ? next.map((item, index) => (
            <div
              key={`next-${index}`}
              className='flex flex-col items-center font-criteria'
              onClick={() => handleItemClick(`next-${index}`)}
            >
              <FlashSaleTab
                item={item}
                index={index}
                type='next'
                title={t('flash_sale_hardcode.banner.next')}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                bgColor='bg-white'
                textColor='text-black'
                borderColor='black'
              />
            </div>
          ))
        : null}
    </div>
  )
}

interface BannerCountDownProps {
  countDownTime: Date | null
  current?: FlashSale[]
  next?: FlashSale[]
  className?: string
  activeTab?: string
  setActiveTab?: (tab: string) => void
}

export const BannerCountDown = ({
  countDownTime,
  current = [],
  next = [],
  className = '',
  activeTab = '',
  setActiveTab,
}: BannerCountDownProps) => {
  const t = useTranslations()
  return (
    // <div
    //   className={`w-full overflow-hidden rounded-2xl bg-gradient-to-l from-[#1D4729] to-[#143025] text-white ${className}`}
    // >
    <div
      className={`w-full overflow-hidden rounded-2xl bg-[url(https://mcdn.coolmate.me/image/2026/03/18/Frame_87998_1.jpg)] bg-cover bg-center bg-no-repeat text-white ${className}`}
    >
      {/* Desktop layout */}
      <div className='hidden items-center justify-between px-[120px] pt-4 md:flex'>
        <div className='flex items-center'>
          <h2 className='text-5xl font-bold'>
            {t('flash_sale_hardcode.banner.title')}
          </h2>
        </div>

        <div className='flex items-center justify-center'>
          <div className='flex items-center space-x-2 font-criteria text-5xl font-bold'>
            {countDownTime && (
              <CountdownTimer expiryTimestamp={countDownTime} />
            )}
          </div>
        </div>

        <div className='flex items-center'>
          <div className='text-center'>
            <div className='mx-auto mb-6 font-criteria text-3xl font-medium uppercase'>
              7TH BIRTHDAY
            </div>
            <FlashSaleList
              current={current}
              next={next}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          </div>
        </div>
      </div>

      {/* Mobile layout */}
      <div className='flex flex-col items-center px-4 pt-4 md:hidden'>
        <div className='mb-3'>
          <h2 className='text-2xl font-bold'>
            {t('flash_sale_hardcode.banner.title')}
          </h2>
        </div>

        <div className='mb-4'>
          <div className='flex items-center space-x-2 text-4xl font-bold'>
            {countDownTime && (
              <CountdownTimer expiryTimestamp={countDownTime} />
            )}
          </div>
        </div>

        <div className='w-full'>
          <div className='grid w-full grid-cols-2 gap-2'>
            {/* Hiển thị các chương trình đang diễn ra */}
            {current.map((item, index) => (
              <FlashSaleTab
                key={`current-${index}`}
                item={item}
                index={index}
                type='current'
                title={t('flash_sale_hardcode.banner.current')}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                bgColor='bg-white'
                textColor='text-black'
                borderColor='border-black'
              />
            ))}

            {/* Hiển thị các chương trình sắp diễn ra */}
            {next.map((item, index) => (
              <FlashSaleTab
                key={`next-${index}`}
                item={item}
                index={index}
                type='next'
                title={t('flash_sale_hardcode.banner.next')}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                bgColor='bg-white'
                textColor='text-black'
                borderColor='black'
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
